import { useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useAdminOrders, type AdminOrderWithItems } from '@/hooks/useAdmin'
import { supabase } from '@/lib/supabase'
import { callEdgeFunction } from '@/lib/edge-functions'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Package,
  ArrowRight,
  Loader2,
  Download,
  Copy,
  ChevronDown,
  RefreshCw,
  Wrench,
  Archive,
  ArchiveRestore,
  Trash2,
} from 'lucide-react'
import { format } from 'date-fns'
import { toast } from 'sonner'
import type { FulfillmentStatus, ShippingAddress } from '@/types/database'

const statusColors: Record<FulfillmentStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  shipped: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
}

const statusFilters = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Processing', value: 'processing' },
  { label: 'Shipped', value: 'shipped' },
  { label: 'Delivered', value: 'delivered' },
]

interface ReconcileCheckoutResponse {
  success: boolean
  scanned: number
  recovered: number
  already_exists: number
  skipped: number
  failed: number
}

function escapeCSV(value: string | number | null | undefined): string {
  const str = String(value ?? '')
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

function serializeCustomInputs(customInputs: Record<string, string> | null): string {
  if (!customInputs) return ''
  return Object.entries(customInputs)
    .map(([key, value]) => {
      // For uploads, just show the filename
      if (value.includes('||')) {
        const [, fileName] = value.split('||')
        return `${key}: ${fileName}`
      }
      return `${key}: ${value}`
    })
    .join('; ')
}

function getOrderDisplayTitle(order: AdminOrderWithItems): string {
  if (order.orderItems && order.orderItems.length > 0) {
    const first = order.orderItems[0].product_title || 'Product'
    if (order.orderItems.length > 1) {
      return `${first} + ${order.orderItems.length - 1} more`
    }
    return first
  }
  return order.product?.title || 'Product'
}

function getOrderDisplayVariant(order: AdminOrderWithItems): string | null {
  if (order.orderItems && order.orderItems.length > 0) {
    return order.orderItems[0].variant_label || null
  }
  return null
}

export function AdminOrdersPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const statusFilter = searchParams.get('status') || 'all'
  const showArchived = searchParams.get('view') === 'archived'
  const { orders, isLoading, refetch } = useAdminOrders(statusFilter, showArchived)

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isBulkUpdating, setIsBulkUpdating] = useState(false)
  const [reconcileSessionId, setReconcileSessionId] = useState('')
  const [isReconcilingPending, setIsReconcilingPending] = useState(false)
  const [isReconcilingSession, setIsReconcilingSession] = useState(false)
  const [isRecoveryOpen, setIsRecoveryOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === orders.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(orders.map(o => o.id)))
    }
  }

  const selectedOrders = orders.filter(o => selectedIds.has(o.id))

  const runReconciliation = async (payload: {
    session_id?: string
    limit?: number
    max_age_hours?: number
  }) => {
    const result = await callEdgeFunction<ReconcileCheckoutResponse>('reconcile-checkout-sessions', {
      method: 'POST',
      body: payload,
      withAuth: true,
    })

    if (result.error || !result.data) {
      throw new Error(result.error || 'Reconciliation failed')
    }

    const data = result.data
    toast.success(
      `Scanned ${data.scanned}: ${data.recovered} recovered, ${data.already_exists} already existed, ${data.skipped} skipped, ${data.failed} failed`
    )
    refetch()
  }

  const reconcilePendingSessions = async () => {
    setIsReconcilingPending(true)
    try {
      await runReconciliation({ limit: 25, max_age_hours: 168 })
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to reconcile pending sessions'
      )
    } finally {
      setIsReconcilingPending(false)
    }
  }

  const reconcileSpecificSession = async () => {
    const sessionId = reconcileSessionId.trim()
    if (!sessionId.startsWith('cs_')) {
      toast.error('Enter a valid Stripe session ID (cs_...)')
      return
    }

    setIsReconcilingSession(true)
    try {
      await runReconciliation({ session_id: sessionId })
      setReconcileSessionId('')
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to reconcile session'
      )
    } finally {
      setIsReconcilingSession(false)
    }
  }

  // Export to CSV — one row per order_item
  const exportToCSV = () => {
    const ordersToExport = selectedOrders.length > 0 ? selectedOrders : orders

    const headers = [
      'Order ID',
      'Order Date',
      'Fulfillment Status',
      'Customer Name',
      'Customer Email',
      'Customer Phone',
      'Ship To Name',
      'Address Line 1',
      'Address Line 2',
      'City',
      'State',
      'ZIP',
      'Country',
      'Product Title',
      'Variant Label',
      'Variant SKU',
      'Quantity',
      'Unit Price',
      'Discount %',
      'Line Total',
      'Custom Inputs',
      'Procurement Status',
      'Vendor Order ID',
      'Vendor Tracking',
      'Order Total',
      'Order Savings',
      'Tracking Number',
      'Tracking URL',
    ]

    const rows: string[][] = []

    for (const order of ordersToExport) {
      const addr = order.shipping_address as ShippingAddress | null
      const orderDate = format(new Date(order.created_at), 'yyyy-MM-dd HH:mm')

      // Common order-level fields
      const orderFields = [
        order.id,
        orderDate,
        order.fulfillment_status,
        order.customer_name || '',
        order.customer_email,
        order.customer_phone || '',
        addr?.name || '',
        addr?.line1 || '',
        addr?.line2 || '',
        addr?.city || '',
        addr?.state || '',
        addr?.postal_code || '',
        addr?.country || '',
      ]

      const orderSuffix = [
        String(order.total_amount),
        String(order.total_savings || ''),
        order.tracking_number || '',
        order.tracking_url || '',
      ]

      if (order.orderItems && order.orderItems.length > 0) {
        // One row per order_item
        for (const item of order.orderItems) {
          rows.push([
            ...orderFields,
            item.product_title || '',
            item.variant_label || '',
            item.variant_sku || '',
            String(item.quantity),
            String(item.unit_price),
            String(item.discount_percent),
            String(item.line_total),
            serializeCustomInputs(item.custom_inputs as Record<string, string> | null),
            item.procurement_status,
            item.manufacturer_order_id || '',
            item.vendor_tracking_number || '',
            ...orderSuffix,
          ])
        }
      } else {
        // Legacy fallback: one row per order
        rows.push([
          ...orderFields,
          order.product?.title || 'N/A',
          '', '', // variant label, sku
          String(order.quantity),
          String(order.unit_price || ''),
          String(order.discount_percent || ''),
          String(order.total_amount),
          '', '', '', '', // custom inputs, procurement, vendor fields
          ...orderSuffix,
        ])
      }
    }

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `orders-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    URL.revokeObjectURL(url)

    const totalRows = rows.length
    toast.success(`Exported ${ordersToExport.length} orders (${totalRows} rows) to CSV`)
  }

  // Copy to clipboard — manufacturer-ready format
  const copyToClipboard = () => {
    const ordersToExport = selectedOrders.length > 0 ? selectedOrders : orders.filter(o => o.fulfillment_status === 'pending')

    const text = ordersToExport.map(order => {
      const addr = order.shipping_address as ShippingAddress | null
      const lines: string[] = [
        `ORDER: ${order.id.slice(0, 8)}`,
        `Date: ${format(new Date(order.created_at), 'MMM d, yyyy h:mm a')}`,
        `Customer: ${order.customer_name || order.customer_email}`,
        `Email: ${order.customer_email}`,
      ]

      if (order.customer_phone) {
        lines.push(`Phone: ${order.customer_phone}`)
      }

      lines.push('')
      lines.push('SHIP TO:')
      if (addr) {
        lines.push(`  ${addr.name}`)
        lines.push(`  ${addr.line1}`)
        if (addr.line2) lines.push(`  ${addr.line2}`)
        lines.push(`  ${addr.city}, ${addr.state} ${addr.postal_code}`)
        lines.push(`  ${addr.country}`)
      } else {
        lines.push('  No shipping address')
      }

      lines.push('')
      lines.push('ITEMS:')

      if (order.orderItems && order.orderItems.length > 0) {
        order.orderItems.forEach((item, i) => {
          lines.push(`  ${i + 1}. ${item.product_title || 'Product'}`)
          if (item.variant_sku) lines.push(`     SKU: ${item.variant_sku}`)
          if (item.variant_label) lines.push(`     Variant: ${item.variant_label}`)
          lines.push(`     Qty: ${item.quantity}`)
          lines.push(`     Price: $${Number(item.line_total).toFixed(2)}`)
          const customInputs = item.custom_inputs as Record<string, string> | null
          if (customInputs && Object.keys(customInputs).length > 0) {
            lines.push(`     Custom: ${serializeCustomInputs(customInputs)}`)
          }
        })
      } else {
        lines.push(`  1. ${order.product?.title || 'Product'}`)
        lines.push(`     Qty: ${order.quantity}`)
        lines.push(`     Price: $${Number(order.total_amount).toFixed(2)}`)
      }

      lines.push('')
      lines.push(`ORDER TOTAL: $${Number(order.total_amount).toFixed(2)}`)
      if (order.total_savings && Number(order.total_savings) > 0) {
        lines.push(`SAVINGS: $${Number(order.total_savings).toFixed(2)}`)
      }
      lines.push('---')

      return lines.join('\n')
    }).join('\n\n')

    navigator.clipboard.writeText(text)
    toast.success(`Copied ${ordersToExport.length} orders to clipboard`)
  }

  // Bulk update status
  const bulkUpdateStatus = async (status: FulfillmentStatus) => {
    if (selectedIds.size === 0) {
      toast.error('No orders selected')
      return
    }

    setIsBulkUpdating(true)
    try {
      const { error } = await supabase
        .from('orders')
        .update({ fulfillment_status: status })
        .in('id', Array.from(selectedIds))

      if (error) throw error

      toast.success(`Updated ${selectedIds.size} orders to ${status}`)
      setSelectedIds(new Set())
      refetch()
    } catch (error) {
      console.error('Bulk update error:', error)
      toast.error('Failed to update orders')
    } finally {
      setIsBulkUpdating(false)
    }
  }

  // Bulk archive/unarchive
  const bulkArchive = async (archive: boolean) => {
    if (selectedIds.size === 0) {
      toast.error('No orders selected')
      return
    }

    setIsBulkUpdating(true)
    try {
      const { error } = await supabase
        .from('orders')
        .update({ is_archived: archive })
        .in('id', Array.from(selectedIds))

      if (error) throw error

      toast.success(`${archive ? 'Archived' : 'Unarchived'} ${selectedIds.size} orders`)
      setSelectedIds(new Set())
      refetch()
    } catch (error) {
      console.error('Bulk archive error:', error)
      toast.error(`Failed to ${archive ? 'archive' : 'unarchive'} orders`)
    } finally {
      setIsBulkUpdating(false)
    }
  }

  // Bulk delete (archived orders only)
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return

    setIsDeleting(true)
    try {
      // Delete order_items first (foreign key constraint)
      const { error: itemsError } = await supabase
        .from('order_items')
        .delete()
        .in('order_id', Array.from(selectedIds))

      if (itemsError) throw itemsError

      const { error } = await supabase
        .from('orders')
        .delete()
        .in('id', Array.from(selectedIds))

      if (error) throw error

      toast.success(`Permanently deleted ${selectedIds.size} orders`)
      setSelectedIds(new Set())
      setIsDeleteDialogOpen(false)
      refetch()
    } catch (error) {
      console.error('Bulk delete error:', error)
      toast.error('Failed to delete orders')
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Orders | Admin</title>
      </Helmet>
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Orders</h1>
            <p className="text-muted-foreground mt-1">
              Manage and fulfill customer orders
            </p>
          </div>

          {/* Export + Recovery Actions */}
          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            <Button variant="outline" size="sm" onClick={copyToClipboard} className="flex-1 sm:flex-none">
              <Copy className="mr-2 size-4" />
              Copy {selectedOrders.length > 0 ? `(${selectedOrders.length})` : 'Pending'}
            </Button>
            <Button variant="outline" size="sm" onClick={exportToCSV} className="flex-1 sm:flex-none">
              <Download className="mr-2 size-4" />
              Export CSV {selectedOrders.length > 0 && `(${selectedOrders.length})`}
            </Button>
            <Dialog open={isRecoveryOpen} onOpenChange={setIsRecoveryOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
                  <Wrench className="mr-2 size-4" />
                  Recovery
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Checkout Recovery</DialogTitle>
                  <DialogDescription>
                    Recover paid Stripe sessions that did not persist to orders.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Button
                      variant="outline"
                      onClick={reconcilePendingSessions}
                      disabled={isReconcilingPending}
                      className="w-full"
                    >
                      {isReconcilingPending && (
                        <Loader2 className="mr-2 size-4 animate-spin" />
                      )}
                      Reconcile Pending Sessions
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      Scans up to 25 recent pending checkout payloads (last 7 days).
                    </p>
                  </div>
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium mb-2">Reconcile Specific Session</p>
                    <div className="flex gap-2">
                      <Input
                        value={reconcileSessionId}
                        onChange={(event) => setReconcileSessionId(event.target.value)}
                        placeholder="cs_test_..."
                        className="font-mono text-xs"
                      />
                      <Button
                        onClick={reconcileSpecificSession}
                        disabled={isReconcilingSession}
                      >
                        {isReconcilingSession && (
                          <Loader2 className="mr-2 size-4 animate-spin" />
                        )}
                        Reconcile
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Enter a Stripe session ID for targeted recovery.
                    </p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {statusFilters.map((filter) => (
            <Link
              key={filter.value}
              to={filter.value === 'all' ? '/admin/orders' : `/admin/orders?status=${filter.value}`}
            >
              <Button
                variant={!showArchived && statusFilter === filter.value ? 'default' : 'outline'}
                size="sm"
              >
                {filter.label}
              </Button>
            </Link>
          ))}
          <div className="w-px h-6 bg-border mx-1" />
          <Link to="/admin/orders?view=archived">
            <Button
              variant={showArchived ? 'default' : 'ghost'}
              size="sm"
            >
              <Archive className="mr-2 size-4" />
              Archived
            </Button>
          </Link>
        </div>

        {/* Archived banner */}
        {showArchived && (
          <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 p-3">
            <div className="flex items-center gap-2 text-sm text-amber-800">
              <Archive className="size-4" />
              <span>Viewing archived orders</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/orders')}
              className="text-amber-800 hover:text-amber-900"
            >
              Back to active
            </Button>
          </div>
        )}

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="flex flex-col gap-3 rounded-lg border bg-muted/50 p-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm font-medium">
              {selectedIds.size} selected
            </span>
            <div className="flex w-full flex-wrap gap-2 sm:w-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={isBulkUpdating} className="w-full sm:w-auto">
                    {isBulkUpdating ? (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 size-4" />
                    )}
                    Update Status
                    <ChevronDown className="ml-2 size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuLabel>Update Status</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => bulkUpdateStatus('pending')}>
                    Mark as Pending
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => bulkUpdateStatus('processing')}>
                    Mark as Processing
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => bulkUpdateStatus('shipped')}>
                    Mark as Shipped
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => bulkUpdateStatus('delivered')}>
                    Mark as Delivered
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => bulkUpdateStatus('cancelled')}>
                    Mark as Cancelled
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  {showArchived ? (
                    <>
                      <DropdownMenuItem onClick={() => bulkArchive(false)}>
                        <ArchiveRestore className="mr-2 size-4" />
                        Unarchive Selected
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600 focus:text-red-600"
                        onClick={() => setIsDeleteDialogOpen(true)}
                      >
                        <Trash2 className="mr-2 size-4" />
                        Delete Permanently
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <DropdownMenuItem onClick={() => bulkArchive(true)}>
                      <Archive className="mr-2 size-4" />
                      Archive Selected
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())} className="w-full sm:w-auto">
                Clear Selection
              </Button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete {selectedIds.size} order{selectedIds.size !== 1 ? 's' : ''} permanently?</DialogTitle>
              <DialogDescription>
                This action cannot be undone. Order data, items, and history will be permanently removed.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={bulkDelete} disabled={isDeleting}>
                {isDeleting && <Loader2 className="mr-2 size-4 animate-spin" />}
                Delete Permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{showArchived ? 'Archived Orders' : 'All Orders'}</CardTitle>
                <CardDescription>
                  {orders.length} order{orders.length !== 1 ? 's' : ''} found
                </CardDescription>
              </div>
              {orders.length > 0 && (
                <Checkbox
                  checked={selectedIds.size === orders.length}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all"
                />
              )}
            </div>
          </CardHeader>
          <CardContent>
            {orders.length > 0 ? (
              <div className="space-y-2">
                {orders.map((order) => {
                  const displayTitle = getOrderDisplayTitle(order)
                  const displayVariant = getOrderDisplayVariant(order)

                  return (
                    <div
                      key={order.id}
                      className="rounded-lg border p-4 transition-colors hover:bg-muted/50"
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedIds.has(order.id)}
                          onCheckedChange={() => toggleSelect(order.id)}
                          aria-label={`Select order ${order.id}`}
                          className="mt-1"
                        />

                        <Link
                          to={`/admin/orders/${order.id}`}
                          className="flex min-w-0 flex-1 flex-col gap-2"
                        >
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium truncate">
                                  {order.customer_name || order.customer_email}
                                </p>
                                {order.item_count > 1 && (
                                  <Badge variant="outline" className="text-xs">
                                    {order.item_count} items
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground truncate">
                                {displayTitle}
                              </p>
                              {displayVariant && (
                                <p className="text-xs text-muted-foreground">
                                  {displayVariant}
                                </p>
                              )}
                            </div>

                            <div className="flex items-center justify-between gap-2 sm:block sm:text-right">
                              <Badge className={statusColors[order.fulfillment_status]}>
                                {order.fulfillment_status}
                              </Badge>
                              <p className="text-sm font-medium sm:mt-1">
                                ${Number(order.total_amount).toFixed(2)}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(order.created_at), 'MMM d, yyyy h:mm a')}
                            </p>
                            <ArrowRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                          </div>
                        </Link>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Package className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">No orders found</h3>
                <p className="text-muted-foreground mt-1">
                  {showArchived
                    ? 'No archived orders'
                    : statusFilter !== 'all'
                      ? `No ${statusFilter} orders`
                      : 'Orders will appear here when customers make purchases'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
